import "react-data-grid/lib/styles.css";
import { RowRendererProps, RowsChangeData } from "react-data-grid";
import { Data, DataSet, ViewConfig, View } from "../../model/core/Workbook";
import { atom, useAtom, useSetAtom } from "jotai";
import { useAtomsDevtools } from "jotai/devtools";
import { viewAtom } from "../../../datasheets/model/atoms/WorkbookFocusAtoms";
import {
  ViewAndDataSet,
  datasetAtom,
  viewWithDatasetAtom,
} from "datasheets/views/spreadsheet/state/datasetAtom";
import { PropertyDescriptorDialog } from "./PropertyDescriptorDialog";
import useWindowDimensions from "hooks/useWindowDimensions";
import { DraggableRowRenderer } from "./DraggableRowRenderer";
import { selectedRowsAtom } from "./state/selectedRowsAtom";
import { moveRow } from "../../model/logic/moveRow";
import { addNewDescriptorForColumnName } from "datasheets/model/logic/columns";
import {
  SpreadsheetViewConfig,
  generateSpreadsheetViewConfig as generateSpreadsheetConfig,
  viewType,
} from "./SpreadsheetViewConfig";
import {
  TabularDataView,
  Row,
  DSColumn,
} from "datasheets/components/TabularDataView";
import { headerRenderer } from "./HeaderRenderer";
import { ViewFactory, ViewProps } from "datasheets/model/ViewFactory";
import { ViewReplicator } from "datasheets/model/logic/replicate";
import {
  PersistentKeyMapper,
  dictionaryValueMap,
} from "datasheets/model/utils/dictionaryUtils";
import { MenuGroupFactory } from "../common/MenuGroupFactory";
import { EditMenuGroup } from "./EditMenuGroup";
import { getOrCreateOrder } from "datasheets/model/logic/insertDeleteRows";

/////////////////////////////
// Mapping from stored data to Spreadsheet tabular view

/////////////////////////////
// Spreadsheet handlers and react components

/**
 * Start tracking column as a property in the dataset
 * @param view
 * @param dataset
 * @param columnIdx
 * @param columnName
 * @returns
 */
const startTrackingColumn = (
  view: View,
  dataset: DataSet,
  columnIdx: number,
  columnName: string
): { propertyKey: string; view: View; dataset: DataSet } => {
  let properties = { ...dataset.properties };
  const propertyKey = addNewDescriptorForColumnName(properties, columnName);
  dataset.properties = properties;
  (view.config as SpreadsheetViewConfig).columnMapping[columnIdx] = propertyKey;
  return { propertyKey: propertyKey, view: view, dataset: dataset };
};

const applyChanges = (
  rows: Row[],
  changes: RowsChangeData<Row, Row>,
  prevViewWithDataset: ViewAndDataSet
): ViewAndDataSet => {
  const [view, dataset] = prevViewWithDataset;
  const viewConfig = view.config as SpreadsheetViewConfig;
  let newView = { ...view };
  let newDataset = { ...dataset };
  // Retrieve property key for column
  let propertyKey = viewConfig.columnMapping[changes.column.idx];
  // If column doesn't already have a property key, we need to create one
  if (propertyKey === undefined) {
    const retVal = startTrackingColumn(
      newView,
      newDataset,
      changes.column.idx,
      changes.column.name as string
    );
    newView = retVal.view;
    newDataset = retVal.dataset;
    propertyKey = retVal.propertyKey;
  }
  // Update rows of data into data objects
  let data = { ...dataset.data };
  let order = getOrCreateOrder(newDataset);
  changes.indexes.forEach((changedRow) => {
    const targetRow = rows[changedRow].objectKey;
    let newRow = targetRow in data ? { ...data[targetRow] } : {};
    newRow[propertyKey] = rows[changedRow][changes.column.idx];
    const change: Data = { [targetRow]: newRow };
    data = { ...data, ...change };
    order = { ...order, ...{ [changedRow]: targetRow } };
  });
  newDataset.data = data;
  newDataset.naturalOrder = order;
  return [newView, newDataset];
};

interface SpreadsheetProps {
  enableVirtualisation?: boolean;
}

const viewConfigAtom = atom(
  (get) => {
    const view = get(viewAtom);
    return view.config as SpreadsheetViewConfig;
  },
  (get, set, arg) => {
    const viewConfig = (
      typeof arg === "function" ? arg(get(viewConfigAtom)) : arg
    ) as SpreadsheetViewConfig;
    const view = get(viewAtom);
    const newView = {
      ...view,
      ...{
        viewConfig: viewConfig,
      },
    };
    set(viewAtom, newView);
  }
);

function Spreadsheet(props: SpreadsheetProps) {
  const setViewWithDataset = useSetAtom(viewWithDatasetAtom);
  const setDataset = useSetAtom(datasetAtom);
  const { width, height } = useWindowDimensions();
  useAtomsDevtools("test");

  const handleChanges = (rows: Row[], changes: RowsChangeData<Row, Row>) => {
    setViewWithDataset((prev: ViewAndDataSet) =>
      applyChanges(rows, changes, prev)
    );
  };

  const renderRow = (key: React.Key, props: RowRendererProps<Row, Row>) => {
    function onRowReorder(fromIndex: number, toIndex: number) {
      if (fromIndex === toIndex) {
        return;
      }
      setDataset(moveRow(fromIndex, toIndex));
    }

    return (
      <DraggableRowRenderer key={key} {...props} onRowReorder={onRowReorder} />
    );
  };

  const [selectedRows, setSelectedRows] = useAtom(selectedRowsAtom);
  const onRowClick = (row: Row, column: DSColumn) => {
    // Check for ctrl or shift
    const e = window.event as MouseEvent;
    if (e.shiftKey) {
      /*shift is down, select range from current selection to new row*/
      setSelectedRows((selection) => {
        const newSelection = [...selection];
        newSelection.sort();
        if (row.rowId < newSelection[0]) {
          for (let i = row.rowId; i < newSelection[0]; i++) {
            if (!newSelection.includes(i)) {
              newSelection.push(i);
            }
          }
        } else {
          let startPos = 0;
          newSelection.forEach((pos) => {
            if (row.rowId > pos) {
              startPos = pos + 1;
              return;
            }
          });
          for (let i = startPos; i <= row.rowId; i++) {
            if (!newSelection.includes(i)) {
              newSelection.push(i);
            }
          }
        }
        return newSelection;
      });
    } else if (e.metaKey || e.ctrlKey) {
      /*ctrl is down*/
      setSelectedRows((selection) => {
        const newSelection = [...selection];
        if (newSelection.includes(row.rowId)) {
          newSelection.splice(newSelection.indexOf(row.rowId), 1);
        } else {
          newSelection.push(row.rowId);
        }
        return newSelection;
      });
    } else {
      // If single selection clear existing selections
      setSelectedRows((selection) => {
        if (selection.includes(row.rowId)) {
          return [];
        } else {
          return [row.rowId];
        }
      });
    }
  };
  return (
    <>
      <TabularDataView
        width={width}
        height={height}
        onRowsChange={handleChanges}
        onRowClick={onRowClick}
        selectedRows={new Set<number>(selectedRows)}
        renderRow={renderRow}
        headerRenderer={headerRenderer}
        className="Spreadsheet"
        enableVirtualization={!!props.enableVirtualisation}
        tabularConfigAtom={viewConfigAtom}
      />
      <PropertyDescriptorDialog />
    </>
  );
}

function component(props: ViewProps) {
  const enableVirtualisation = props.testMode ? false : true;
  return <Spreadsheet enableVirtualisation={enableVirtualisation} />;
}

const register = {
  register: function register() {
    ViewReplicator.addReplicator(
      "spreadsheet",
      (
        view: ViewConfig,
        propertyRemap: PersistentKeyMapper
      ): SpreadsheetViewConfig => {
        const spreadsheetView = view as SpreadsheetViewConfig;
        const type = "spreadsheet";
        const columnMapping = dictionaryValueMap(
          spreadsheetView.columnMapping,
          (id: string) => propertyRemap.map(id)
        );
        const columnDisplayProperties = {
          ...spreadsheetView.columnDisplayProperties,
        };
        const datasetId = propertyRemap.map(spreadsheetView.datasetId);
        return { type, columnDisplayProperties, columnMapping, datasetId };
      }
    );
    MenuGroupFactory.addMenuGroup(
      "spreadsheet",
      "spreadsheet.Edit",
      <EditMenuGroup key="spreadsheet.Edit" />
    );
    ViewFactory.addView(viewType, generateSpreadsheetConfig, component);
  },
};

export default register;
